import type {MediaPriority, ReceiverJitterBufferTarget} from '@pexip/infinity';
import {
    isJitterBufferTarget,
    isMediaPriority,
} from '@pexip/infinity/typeGuards';
import type {LogLevelsString} from '@pexip/logger';
import type {IncludeExcludeConstraint} from '@pexip/media-control';
import {INCLUDE_EXCLUDE_CONSTRAINT} from '@pexip/media-control/constants';

import {
    shouldEnableAudioProcessing,
    shouldEnableVideoProcessing,
} from './config';
import type {StringQuadruple} from './constants';
import {
    AVAILABLE_CALL_TYPES,
    BACKGROUND_BLUR_AMOUNT_HIGH,
    BACKGROUND_BLUR_AMOUNT_LOW,
    BANDWIDTHS,
    BG_IMAGE_URL,
    DEFAULT_FEEDBACK_CONFIG,
    FRAME_RATE,
    PRESENTATION_JITTER_BUFFER_TARGET_MS,
    PRIVACY_URL,
    SEGMENTATION_MODEL,
    TERMS_URL,
    TRANSFER_TIMEOUT_S,
} from './constants';
import type {DeploymentConfig} from './deploymentConfig';
import {parseDeploymentConfig} from './deploymentConfig';
import {levelSelectedByHost, logger, rootLogger} from './logger';
import type {CallHistoryConfig, FeedbackConfig, TestIdValue} from './types';
import {getClientCallType} from './utils/getClientCallType';
import {isClientCallType} from './utils/isClientCallType';

export const deploymentConfig = ((): DeploymentConfig => {
    const json = document.querySelector('#ic-config')?.textContent;
    if (json === undefined || json === null) {
        return {};
    }
    try {
        return parseDeploymentConfig(json);
    } catch (error: unknown) {
        // Fail closed: an invalid configuration disables what it configures.
        logger.error(error, 'failed to parse deploymentConfig');
        return {};
    }
})();

export const applicationConfig = {
    ...deploymentConfig,
    availableCallTypes: AVAILABLE_CALL_TYPES,
    audioProcessing: shouldEnableAudioProcessing(),
    bandwidths: BANDWIDTHS,
    frameRate: FRAME_RATE,
    node: window.location.host,
    segmentationModel: SEGMENTATION_MODEL,
    dynamicVideoProcessingDimensions: false,
    showLiveCaptionsFeature: true,
    videoProcessing: shouldEnableVideoProcessing(),
    shouldMaskConference: false,
    directMedia: true,
    maxPluginToolbarButtons: undefined as number | undefined,
    termsAndConditions: {
        en: TERMS_URL,
    } as Record<string, string>,
    privacyPolicy: {
        en: PRIVACY_URL,
    } as Record<string, string>,
    disconnectDestination: '',
    bgImageAssets: [BG_IMAGE_URL],
    showTermsAndConditionsLink: true,
    monitorTypeSurfaces:
        INCLUDE_EXCLUDE_CONSTRAINT.Include as IncludeExcludeConstraint,
    selfBrowserSurface:
        INCLUDE_EXCLUDE_CONSTRAINT.Exclude as IncludeExcludeConstraint,
    systemAudio: INCLUDE_EXCLUDE_CONSTRAINT.Include as IncludeExcludeConstraint,
    hiddenFunctionality: {} as Record<TestIdValue, true>,
    enableFeedback: true,
    feedback: DEFAULT_FEEDBACK_CONFIG,
    /**
     * Same as `networkPriorities` for backward compatibility
     * @see {@link https://gitlab.com/pexip/zoo/-/issues/5042#note_2302767328}
     */
    mediaPriorities: {
        video: 'low',
        audio: 'low',
        presentation: 'low',
    } satisfies MediaPriority as MediaPriority,
    networkPriorities: undefined as MediaPriority | undefined,
    encodingPriorities: {
        video: 'low',
        audio: 'medium',
        presentation: 'medium',
    } satisfies MediaPriority as MediaPriority,
    jitterBufferTarget: {
        presentation: PRESENTATION_JITTER_BUFFER_TARGET_MS,
    } satisfies ReceiverJitterBufferTarget as
        ReceiverJitterBufferTarget | undefined,
    transferTimeout: TRANSFER_TIMEOUT_S,
    logLevel: undefined as undefined | LogLevelsString,
    showMeetingTitleInHeader: true,
    callHistory: {
        enableRecents: true,
        enableFavorites: true,
        favorites: [],
    } as CallHistoryConfig,
    backgroundBlurAmountHigh: BACKGROUND_BLUR_AMOUNT_HIGH,
    backgroundBlurAmountLow: BACKGROUND_BLUR_AMOUNT_LOW,
    sendCallStatisticsEvents: false,
};
export type ApplicationConfig = typeof applicationConfig;

/**
 * Parses a toolbar button limit as a non-negative integer.
 */
const parseMaxPluginToolbarButtons = (value: unknown): number | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        logger.warn(
            {value},
            'Invalid maxPluginToolbarButtons: expected a non-negative integer',
        );
        return undefined;
    }
    return value;
};

export const setApplicationConfig = <K extends keyof ApplicationConfig>(
    key: K,
    value: ApplicationConfig[K],
) => {
    switch (key) {
        case 'encodingPriorities': {
            if (isMediaPriority(value)) {
                applicationConfig.encodingPriorities = value;
            }
            break;
        }
        case 'networkPriorities':
        case 'mediaPriorities': {
            if (isMediaPriority(value)) {
                applicationConfig.networkPriorities = value;
            }
            break;
        }
        case 'jitterBufferTarget': {
            if (isJitterBufferTarget(value)) {
                applicationConfig.jitterBufferTarget = value;
            }
            break;
        }
        case 'bandwidths':
            if (Array.isArray(value)) {
                const accepted = value
                    .flatMap(v => {
                        const n = +v;
                        // 0 is not a valid bandwidth
                        if (n) {
                            return [v];
                        }
                        return [];
                    })
                    .sort((a, b) => +a - +b)
                    .slice(0, BANDWIDTHS.length);
                if (accepted.length === BANDWIDTHS.length) {
                    applicationConfig.bandwidths = accepted as StringQuadruple;
                }
            }
            break;
        case 'feedback': {
            // If the feedback url is not provided, use the default one
            if ((value as FeedbackConfig).url === undefined) {
                applicationConfig.feedback.url = DEFAULT_FEEDBACK_CONFIG.url;
            } else {
                applicationConfig.feedback.url = (value as FeedbackConfig).url;
            }
            // If the enable feedback is not provided, use the default one
            if ((value as FeedbackConfig).enable === undefined) {
                applicationConfig.feedback.enable =
                    DEFAULT_FEEDBACK_CONFIG.enable;
            } else {
                applicationConfig.feedback.enable = (
                    value as FeedbackConfig
                ).enable;
            }
            break;
        }
        case 'callHistory': {
            const callHistoryValue = value as CallHistoryConfig;
            if (callHistoryValue.enableRecents !== undefined) {
                applicationConfig.callHistory.enableRecents =
                    callHistoryValue.enableRecents;
            }
            if (callHistoryValue.enableFavorites !== undefined) {
                applicationConfig.callHistory.enableFavorites =
                    callHistoryValue.enableFavorites;
            }
            if (Array.isArray(callHistoryValue.favorites)) {
                applicationConfig.callHistory.favorites =
                    callHistoryValue.favorites;
            }
            break;
        }
        case 'logLevel':
            // A level selected by the host is fixed for the page load.
            if (levelSelectedByHost) {
                break;
            }
            if (value && typeof value === 'string') {
                logger.debug({value}, 'Change log level');
                rootLogger.level = value;
            }
            applicationConfig[key] = value;
            break;

        case 'availableCallTypes':
            if (Array.isArray(value)) {
                applicationConfig.availableCallTypes = value.flatMap(
                    callType => {
                        if (typeof callType === 'string') {
                            return getClientCallType(callType) ?? [];
                        }
                        return isClientCallType(callType) ? [callType] : [];
                    },
                );
            }
            break;
        case 'videoProcessing':
            // Branding may disable video processing but cannot enable it on
            // devices or browsers that do not support it.
            applicationConfig.videoProcessing =
                value === true && shouldEnableVideoProcessing();
            break;
        case 'maxPluginToolbarButtons': {
            const maxPluginToolbarButtons = parseMaxPluginToolbarButtons(value);
            if (maxPluginToolbarButtons !== undefined) {
                applicationConfig.maxPluginToolbarButtons =
                    maxPluginToolbarButtons;
            }
            break;
        }
        default:
            applicationConfig[key] = value;
            break;
    }
};
